import * as t from "io-ts";
import { X25519PublicKeyFromBase64 } from "../extensions/io-ts/X25519PublicKeyFromBase64";
import { X25519SecretKeyFromBase64 } from "../extensions/io-ts/X25519SecretKeyFromBase64";
import { PlainPassword } from "./user/password";
import { newSalt, Salt, saltToBuffer } from "./user/salt";
import getSodium from "./../sodium"
import { UnexpectedValueError } from "../unexpected-value-error";
import { X25519PublicKey, X25519SecretKey } from "sodium-plus";
import { AesCipherKey, AesIv, AesKeyWithIvData } from "./crypto/aes-key-with-iv";
import { Base64, Base64Url } from "../extensions/io-ts/Base64";
import { PlainMemberEmail } from "./user/memberEmail";
import { FormId } from "./ids";

const PlainSecretKey = t.string.pipe(X25519SecretKeyFromBase64);
type PlainSecretKey = t.TypeOf<typeof PlainSecretKey>;
const PlainPublicKey = t.string.pipe(X25519PublicKeyFromBase64);
type PlainPublicKey = t.TypeOf<typeof PlainPublicKey>;
const EncryptedKeyBase64 = Base64;
type EncryptedKeyBase64 = t.TypeOf<typeof EncryptedKeyBase64>;
const EncryptedKeyBase64Url = Base64Url;
type EncryptedKeyBase64Url = t.TypeOf<typeof EncryptedKeyBase64Url>;

// Api-Schlüssel werden für die sichere Kommunikation über die Server-Endpunkte verwendet
interface SecretApiKeyBrand {
  readonly SecretApiKey: unique symbol;
}
export const SecretApiKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, SecretApiKeyBrand> => true,
  "SecretApiKey");
export type SecretApiKey = t.TypeOf<typeof SecretApiKey>;

let secretApiKey: X25519SecretKey | null | undefined = undefined;

export function getSecretApiKey() {
  if (secretApiKey !== undefined) return secretApiKey;

  try {
    return secretApiKey =
      X25519SecretKey.from(
        process.env.SECRET_API_KEY ?? "",
        "hex"
      );
  } catch (err) {
    console.error(`Could not load secret api key: ${err + ''}`);
    return secretApiKey = null;
  }
}

interface PublicApiKeyBrand {
  readonly PublicApiKey: unique symbol;
}
export const PublicApiKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicApiKeyBrand> => true,
  "PublicApiKey");
export type PublicApiKey = t.TypeOf<typeof PublicApiKey>;

let publicApiKey: X25519PublicKey | null | undefined = undefined;

export function getPublicApiKey(): X25519PublicKey | null {
  if (publicApiKey !== undefined) return publicApiKey;

  try {
    return publicApiKey =
      X25519PublicKey.from(
        process.env.NEXT_PUBLIC_API_KEY ?? "",
        "hex"
      );
  } catch (err) {
    console.error(`Could not load public api key: ${err + ''}`);
    return publicApiKey = null;
  }
}

export interface ApiKeypair {
  secretKey: SecretApiKey;
  publicKey: PublicApiKey;
}

// Data-Schlüssel werden für die sichere Ablage in der Datenbank und Datenspeicher verwendet
interface SecretDataKeyBrand {
  readonly SecretDataKey: unique symbol;
}
export const SecretDataKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, SecretDataKeyBrand> => true,
  "SecretDataKey");
export type SecretDataKey = t.TypeOf<typeof SecretDataKey>;

let secretDataKey: X25519SecretKey | null | undefined = undefined;

export function getSecretDataKey(): X25519SecretKey | null {
  if (secretDataKey !== undefined) return secretDataKey;

  try {
    return secretDataKey =
      X25519SecretKey.from(
        process.env.SECRET_DATA_KEY ?? "",
        "hex"
      );
  } catch (err) {
    console.error(`Could not load secret data key: ${err + ''}`);
    return secretDataKey = null;
  }
}

interface PublicDataKeyBrand {
  readonly PublicDataKey: unique symbol;
}
export const PublicDataKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicDataKeyBrand> => true,
  "PublicDataKey");
export type PublicDataKey = t.TypeOf<typeof PublicDataKey>;

let publicDataKey: X25519PublicKey | null | undefined = undefined;

export function getPublicDataKey(): X25519PublicKey | null {
  if (publicDataKey !== undefined) return publicDataKey;

  try {
    return publicDataKey =
      X25519PublicKey.from(
        process.env.PUBLIC_DATA_KEY ?? "",
        "hex"
      );
  } catch (err) {
    console.error(`Could not load public data key: ${err + ''}`);
    return publicDataKey = null;
  }
}

export interface DataKeypair {
  secretKey: SecretDataKey;
  publicKey: PublicDataKey;
}

// Aus dem Passwort des Studiobetreibers wird ein Schlüssel abgeleitet, mit dem der generierte private Schlüssel des Studiobetreibers verschlüsselt wird
const UserPassword = PlainPassword;
type UserPassword = PlainPassword;

// UserDerived-Schlüssel ermöglich den Zugriff auf den privaten Schlüssel des Studiobetreibers
// Der abgeleitete geheime Schlüssel wird mit dem ServerApiKey verschlüsselt im Jwt-Cookie abgelegt
interface UserDerivedKeyParametersBrand {
  readonly UserDerivedKeyParameters: unique symbol;
}
const UserDerivedKeyParametersData = t.type({
  salt: Salt,
  opslimit: t.Int,
  memlimit: t.Int,
  algorithm: t.Int,
});
type UserDerivedKeyParametersData = t.TypeOf<typeof UserDerivedKeyParametersData>;

export const UserDerivedKeyParameters = t.brand(UserDerivedKeyParametersData,
  (n): n is t.Branded<UserDerivedKeyParametersData, UserDerivedKeyParametersBrand> => true,
  "UserDerivedKeyParameters");
export type UserDerivedKeyParameters = t.TypeOf<typeof UserDerivedKeyParameters>;

export async function generateUserDerivedKeyParameters(): Promise<UserDerivedKeyParameters> {
  const sodium = await getSodium();
  return UnexpectedValueError.throwIfNot("generate derive parameters",
    UserDerivedKeyParameters, {
    salt: newSalt(),
    opslimit: sodium.CRYPTO_PWHASH_OPSLIMIT_INTERACTIVE,
    memlimit: sodium.CRYPTO_PWHASH_MEMLIMIT_INTERACTIVE,
    algorithm: sodium.CRYPTO_PWHASH_ALG_ARGON2ID13,
  });
}

interface PlainSecretUserDerivedKeyBrand {
  readonly PlainSecretUserDerivedKey: unique symbol;
}
export const PlainSecretUserDerivedKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, PlainSecretUserDerivedKeyBrand> => true,
  "PlainSecretUserDerivedKey");
export type PlainSecretUserDerivedKey = t.TypeOf<typeof PlainSecretUserDerivedKey>;

interface EncryptedSecretUserDerivedKeyBrand {
  readonly EncryptedSecretUserDerivedKey: unique symbol;
}
export const EncryptedSecretUserDerivedKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, EncryptedSecretUserDerivedKeyBrand> => true,
  "EncryptedSecretUserDerivedKey");
export type EncryptedSecretUserDerivedKey = t.TypeOf<typeof EncryptedSecretUserDerivedKey>;

export async function encryptSecretUserDerivedKey(serverKey: PublicApiKey, userDerivedKey: PlainSecretUserDerivedKey): Promise<EncryptedSecretUserDerivedKey> {
  const sodium = await getSodium();

  const buffer = userDerivedKey.getBuffer();
  const serverEncrypted = await sodium.crypto_box_seal(buffer, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted user derived key",
    EncryptedSecretUserDerivedKey, serverEncrypted.toString("base64"));
}

export async function decryptSecretUserDerivedKey(serverKeypair: ApiKeypair, userDerivedKey: EncryptedSecretUserDerivedKey): Promise<PlainSecretUserDerivedKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(EncryptedSecretUserDerivedKey.encode(userDerivedKey), "base64");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain user derived key",
    PlainSecretUserDerivedKey, new X25519SecretKey(serverDecrypted));
}

export async function decryptSecretUserDerivedKeyAsPair(serverKeypair: ApiKeypair, userDerivedKey: EncryptedSecretUserDerivedKey): Promise<UserDerivedKeypair> {
  const secretKey = await decryptSecretUserDerivedKey(serverKeypair, userDerivedKey);
  return await createKeypairFromDerivedSecretKey(secretKey);
}

interface PublicUserDerivedKeyBrand {
  readonly PublicUserDerivedKey: unique symbol;
}
export const PublicUserDerivedKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicUserDerivedKeyBrand> => true,
  "PublicUserDerivedKey");
export type PublicUserDerivedKey = t.TypeOf<typeof PublicUserDerivedKey>;

export interface UserDerivedKeypair {
  secretKey: PlainSecretUserDerivedKey;
  publicKey: PublicUserDerivedKey;
}

async function createKeypairFromDerivedSecretKey(secretKey: PlainSecretUserDerivedKey): Promise<UserDerivedKeypair> {
  const sodium = await getSodium();

  const publicKey = await sodium.crypto_box_publickey_from_secretkey(secretKey);
  return {
    secretKey: secretKey,
    publicKey: UnexpectedValueError.throwIfNot("decode derived public key",
      PublicUserDerivedKey, publicKey),
  };
}

export async function deriveKeyFromUserPassword(parameters: UserDerivedKeyParameters, password: UserPassword): Promise<UserDerivedKeypair> {
  const sodium = await getSodium();

  const passwordString = UserPassword.encode(password);
  const saltBuffer = saltToBuffer(parameters.salt);

  const key = await sodium.crypto_pwhash(
    32,
    passwordString,
    saltBuffer,
    parameters.opslimit,
    parameters.memlimit,
    parameters.algorithm,
  );

  return createKeypairFromDerivedSecretKey(
    UnexpectedValueError.throwIfNot("decode derived secret key",
      PlainSecretUserDerivedKey, new X25519SecretKey(key.getBuffer())));
}

// UserData-Schlüssel ermöglichen den Zugriff durch den Studiobetreiber, z. B. auf den verschlüsselten SubmissionData-Schlüssel
interface PlainSecretUserDataKeyBrand {
  readonly PlainSecretUserDataKey: unique symbol;
}
export const PlainSecretUserDataKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, PlainSecretUserDataKeyBrand> => true,
  "PlainSecretUserDataKey");
export type PlainSecretUserDataKey = t.TypeOf<typeof PlainSecretUserDataKey>;

interface EncryptedSecretUserDataKeyBrand {
  readonly EncryptedSecretUserDataKey: unique symbol;
}
export const EncryptedSecretUserDataKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, EncryptedSecretUserDataKeyBrand> => true,
  "EncryptedSecretUserDataKey");
export type EncryptedSecretUserDataKey = t.TypeOf<typeof EncryptedSecretUserDataKey>;

export async function encryptSecretUserDataKey(serverKey: PublicDataKey, userDerivedKey: PublicUserDerivedKey, userDataKey: PlainSecretUserDataKey): Promise<EncryptedSecretUserDataKey> {
  const sodium = await getSodium();

  const buffer = userDataKey.getBuffer();
  const userEncrypted = await sodium.crypto_box_seal(buffer, userDerivedKey);
  const serverEncrypted = await sodium.crypto_box_seal(userEncrypted, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted user data secret key",
    EncryptedSecretUserDataKey, serverEncrypted.toString("base64"));
}

export async function decryptSecretUserDataKey(serverKeypair: DataKeypair, userDerivedKeypair: UserDerivedKeypair, userDataKey: EncryptedSecretUserDataKey): Promise<PlainSecretUserDataKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(EncryptedSecretUserDataKey.encode(userDataKey), "base64");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);
  const userDecrypted = await sodium.crypto_box_seal_open(serverDecrypted, userDerivedKeypair.publicKey, userDerivedKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain user data secret key",
    PlainSecretUserDataKey, new X25519SecretKey(userDecrypted));
}

interface PublicUserDataKeyBrand {
  readonly PublicUserDataKey: unique symbol;
}
export const PublicUserDataKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicUserDataKeyBrand> => true,
  "PublicUserDataKey");
export type PublicUserDataKey = t.TypeOf<typeof PublicUserDataKey>;

export interface UserDataKeypair {
  secretKey: PlainSecretUserDataKey;
  publicKey: PublicUserDataKey;
}

export async function generateUserDataKeypair(): Promise<UserDataKeypair> {
  const sodium = await getSodium();
  const keypair = await sodium.crypto_box_keypair();
  const secretKey = await sodium.crypto_box_secretkey(keypair);
  const publicKey = await sodium.crypto_box_publickey(keypair);

  return {
    secretKey: UnexpectedValueError.throwIfNot("decode user data secret key",
      PlainSecretUserDataKey, secretKey),
    publicKey: UnexpectedValueError.throwIfNot("decode user data public key",
      PublicUserDataKey, publicKey)
  }
}

// SubmissionData-Schlüssel ermöglichen den Zugriff auf die verschlüsselten Antworten des eingereichten Formulars
interface PlainSecretSubmissionDataKeyBrand {
  readonly PlainSecretSubmissionDataKey: unique symbol;
}
export const PlainSecretSubmissionDataKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, PlainSecretSubmissionDataKeyBrand> => true,
  "PlainSecretSubmissionDataKey");
export type PlainSecretSubmissionDataKey = t.TypeOf<typeof PlainSecretSubmissionDataKey>;

interface UserEncryptedSecretSubmissionDataKeyBrand {
  readonly UserEncryptedSecretSubmissionDataKey: unique symbol;
}
export const UserEncryptedSecretSubmissionDataKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, UserEncryptedSecretSubmissionDataKeyBrand> => true,
  "UserEncryptedSecretSubmissionDataKey");
export type UserEncryptedSecretSubmissionDataKey = t.TypeOf<typeof UserEncryptedSecretSubmissionDataKey>;

export async function encryptSecretSubmissionDataKeyByUser(serverKey: PublicDataKey, userKey: PublicUserDataKey, submissionKey: PlainSecretSubmissionDataKey): Promise<UserEncryptedSecretSubmissionDataKey> {
  const sodium = await getSodium();

  const buffer = submissionKey.getBuffer();
  const userEncrypted = await sodium.crypto_box_seal(buffer, userKey);
  const serverEncrypted = await sodium.crypto_box_seal(userEncrypted, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted submission secret key by user",
    UserEncryptedSecretSubmissionDataKey, serverEncrypted.toString("base64"));
}

export async function decryptSecretSubmissionDataKeyByUser(serverKeypair: DataKeypair, userKeypair: UserDataKeypair, submissionKey: UserEncryptedSecretSubmissionDataKey): Promise<PlainSecretSubmissionDataKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(UserEncryptedSecretSubmissionDataKey.encode(submissionKey), "base64");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);
  const userDecrypted = await sodium.crypto_box_seal_open(serverDecrypted, userKeypair.publicKey, userKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain submission secret key by user",
    PlainSecretSubmissionDataKey, new X25519SecretKey(userDecrypted));
}

interface LinkEncryptedSecretSubmissionDataKeyBrand {
  readonly LinkEncryptedSecretSubmissionDataKey: unique symbol;
}
export const LinkEncryptedSecretSubmissionDataKey = t.brand(EncryptedKeyBase64Url,
  (n): n is t.Branded<EncryptedKeyBase64Url, LinkEncryptedSecretSubmissionDataKeyBrand> => true,
  "LinkEncryptedSecretSubmissionDataKey");
export type LinkEncryptedSecretSubmissionDataKey = t.TypeOf<typeof LinkEncryptedSecretSubmissionDataKey>;

export async function encryptSecretSubmissionDataKeyByLink(serverKey: PublicApiKey, linkKey: PublicSubmissionLinkKey, submissionKey: PlainSecretSubmissionDataKey): Promise<LinkEncryptedSecretSubmissionDataKey> {
  const sodium = await getSodium();

  const buffer = submissionKey.getBuffer();
  const linkEncrypted = await sodium.crypto_box_seal(buffer, linkKey);
  const serverEncrypted = await sodium.crypto_box_seal(linkEncrypted, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted submission secret key by link",
    LinkEncryptedSecretSubmissionDataKey, serverEncrypted.toString("base64url"));
}

export async function decryptSecretSubmissionDataKeyByLink(serverKeypair: ApiKeypair, linkKeypair: SubmissionLinkKeypair, submissionKey: LinkEncryptedSecretSubmissionDataKey): Promise<PlainSecretSubmissionDataKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(LinkEncryptedSecretSubmissionDataKey.encode(submissionKey), "base64url");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);
  const linkDecrypted = await sodium.crypto_box_seal_open(serverDecrypted, linkKeypair.publicKey, linkKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain submission secret key by link",
    PlainSecretSubmissionDataKey, new X25519SecretKey(linkDecrypted));
}

interface PublicSubmissionDataKeyBrand {
  readonly PublicSubmissionDataKey: unique symbol;
}
export const PublicSubmissionDataKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicSubmissionDataKeyBrand> => true,
  "PublicSubmissionDataKey");
export type PublicSubmissionDataKey = t.TypeOf<typeof PublicSubmissionDataKey>;

export interface SubmissionDataKeypair {
  secretKey: PlainSecretSubmissionDataKey;
  publicKey: PublicSubmissionDataKey;
}

export async function generateSubmissionDataKeypair(): Promise<SubmissionDataKeypair> {
  const sodium = await getSodium();
  const keypair = await sodium.crypto_box_keypair();
  const secretKey = await sodium.crypto_box_secretkey(keypair);
  const publicKey = await sodium.crypto_box_publickey(keypair);

  return {
    secretKey: UnexpectedValueError.throwIfNot("decode submission data secret key",
      PlainSecretSubmissionDataKey, secretKey),
    publicKey: UnexpectedValueError.throwIfNot("decode submission data public key",
      PublicSubmissionDataKey, publicKey)
  }
}

// SubmissionSymmetric-Schlüssel wird für die Verschlüsselung von großen Datenmengen (Dateien) verwendet
interface PlainSubmissionSymmetricDataKeyBrand {
  readonly PlainSubmissionSymmetricDataKey: unique symbol;
}
export const PlainSubmissionSymmetricDataKey = t.brand(AesKeyWithIvData,
  (n): n is t.Branded<AesKeyWithIvData, PlainSubmissionSymmetricDataKeyBrand> => true,
  "PlainSubmissionSymmetricDataKey");
export type PlainSubmissionSymmetricDataKey = t.TypeOf<typeof PlainSubmissionSymmetricDataKey>;

interface EncryptedSubmissionSymmetricDataKeyBrand {
  readonly EncryptedSubmissionSymmetricDataKey: unique symbol;
}
export const EncryptedSubmissionSymmetricDataKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, EncryptedSubmissionSymmetricDataKeyBrand> => true,
  "EncryptedSubmissionSymmetricDataKey");
export type EncryptedSubmissionSymmetricDataKey = t.TypeOf<typeof EncryptedSubmissionSymmetricDataKey>;

export async function encryptSubmissionSymmetricDataKey(serverKey: PublicDataKey, submissionKey: PublicSubmissionDataKey, symmetricKey: PlainSubmissionSymmetricDataKey): Promise<EncryptedSubmissionSymmetricDataKey> {
  const sodium = await getSodium();

  const symmetricKeyData = PlainSubmissionSymmetricDataKey.encode(symmetricKey);
  const symmetricKeyJson = JSON.stringify(symmetricKeyData);
  const symmetricKeyBuffer = Buffer.from(symmetricKeyJson, "utf8");

  const userEncrypted = await sodium.crypto_box_seal(symmetricKeyBuffer, submissionKey);
  const serverEncrypted = await sodium.crypto_box_seal(userEncrypted, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted submission secret key",
    EncryptedSubmissionSymmetricDataKey, serverEncrypted.toString("base64"));
}

export async function decryptSubmissionSymmetricDataKey(serverKeypair: DataKeypair, submissionKey: SubmissionDataKeypair, symmetricKey: EncryptedSubmissionSymmetricDataKey): Promise<PlainSubmissionSymmetricDataKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(symmetricKey, "base64");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);
  const submissionDecrypted = await sodium.crypto_box_seal_open(serverDecrypted, submissionKey.publicKey, submissionKey.secretKey);

  const submissionKeyJson = submissionDecrypted.toString("utf8");
  const submissionKeyData = JSON.parse(submissionKeyJson);

  return UnexpectedValueError.throwIfNot("decode submission symmetric key",
    PlainSubmissionSymmetricDataKey, submissionKeyData);
}

export async function generateSubmissionSymmetricDataKey(): Promise<PlainSubmissionSymmetricDataKey> {
  const sodium = await getSodium();
  const key = (await sodium.randombytes_buf(256 / 8)).toString("base64");
  const iv = (await sodium.randombytes_buf(16)).toString("base64");
  return UnexpectedValueError.throwIfNot("decode submission symmetric key + iv",
    PlainSubmissionSymmetricDataKey, {
    key: UnexpectedValueError.throwIfNot("decode submission symmetric key",
      AesCipherKey, key),
    iv: UnexpectedValueError.throwIfNot("decode submission symmetric iv",
      AesIv, iv)
  });
}

// SubmissionLink-Schlüsselpaar wird für die Verschlüsselung vom Submission-Datenschlüssel verwendet, damit dieser in Links innerhalb von Emails übertragen werden kann
interface PlainSecretSubmissionLinkKeyBrand {
  readonly PlainSecretSubmissionLinkKey: unique symbol;
}
export const PlainSecretSubmissionLinkKey = t.brand(PlainSecretKey,
  (n): n is t.Branded<PlainSecretKey, PlainSecretSubmissionLinkKeyBrand> => true,
  "PlainSecretSubmissionLinkKey");
export type PlainSecretSubmissionLinkKey = t.TypeOf<typeof PlainSecretSubmissionLinkKey>;

interface EncryptedSecretSubmissionLinkKeyBrand {
  readonly EncryptedSecretSubmissionLinkKey: unique symbol;
}
export const EncryptedSecretSubmissionLinkKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, EncryptedSecretSubmissionLinkKeyBrand> => true,
  "EncryptedSecretSubmissionLinkKey");
export type EncryptedSecretSubmissionLinkKey = t.TypeOf<typeof EncryptedSecretSubmissionLinkKey>;

export async function encryptSecretSubmissionLinkKey(serverKey: PublicDataKey, submissionLinkKey: PlainSecretSubmissionLinkKey): Promise<EncryptedSecretSubmissionLinkKey> {
  const sodium = await getSodium();

  const buffer = submissionLinkKey.getBuffer();
  const encrypted = await sodium.crypto_box_seal(buffer, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted submission link secret key",
    EncryptedSecretSubmissionLinkKey, encrypted.toString("base64"));
}

export async function decryptSecretSubmissionLinkKey(serverKeypair: DataKeypair, submissionLinkKey: EncryptedSecretSubmissionLinkKey): Promise<PlainSecretSubmissionLinkKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(EncryptedSecretSubmissionLinkKey.encode(submissionLinkKey), "base64");
  const decrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain submission link secret key",
    PlainSecretSubmissionLinkKey, new X25519SecretKey(decrypted));
}

interface PublicSubmissionLinkKeyBrand {
  readonly PublicSubmissionLinkKey: unique symbol;
}
export const PublicSubmissionLinkKey = t.brand(PlainPublicKey,
  (n): n is t.Branded<PlainPublicKey, PublicSubmissionLinkKeyBrand> => true,
  "PublicSubmissionLinkKey");
export type PublicSubmissionLinkKey = t.TypeOf<typeof PublicSubmissionLinkKey>;

export interface SubmissionLinkKeypair {
  secretKey: PlainSecretSubmissionLinkKey;
  publicKey: PublicSubmissionLinkKey;
}

export async function generateSubmissionLinkKeypair(): Promise<SubmissionLinkKeypair> {
  const sodium = await getSodium();
  const keypair = await sodium.crypto_box_keypair();
  const secretKey = await sodium.crypto_box_secretkey(keypair);
  const publicKey = await sodium.crypto_box_publickey(keypair);

  return {
    secretKey: UnexpectedValueError.throwIfNot("decode submission link secret key",
      PlainSecretSubmissionLinkKey, secretKey),
    publicKey: UnexpectedValueError.throwIfNot("decode submission link public key",
      PublicSubmissionLinkKey, publicKey)
  }
}

// Ermöglicht zeitlich begrenzten Zugriff auf die Liste der eingereichten Formulare 
// für das angegebene Formular und die Emailadresse des Mitglieds.
// In dem Datenblock steht nur die Email des Empfängers drin, weshalb eine einfache
// Verschlüsselung mit API-Key ausreichen sollte. Es dient einfach nur dazu, dass
// wir nur Tokens akzeptieren, die von unserem Server erzeugt wurden.
export const CancelSubmissionDataInfo = t.type({
  formId: FormId,
  email: PlainMemberEmail,
  expiresAt: t.string,
});
export type CancelSubmissionDataInfo = t.TypeOf<typeof CancelSubmissionDataInfo>;

interface PlainCancelSubmissionDataBrand {
  readonly PlainCancelSubmissionData: unique symbol;
}
export const PlainCancelSubmissionData = t.brand(CancelSubmissionDataInfo,
  (n): n is t.Branded<CancelSubmissionDataInfo, PlainCancelSubmissionDataBrand> => true,
  "PlainCancelSubmissionData");
export type PlainCancelSubmissionData = t.TypeOf<typeof PlainCancelSubmissionData>;

export function createPlainCancelSubmissionData(info: CancelSubmissionDataInfo): PlainCancelSubmissionData {
  return UnexpectedValueError.throwIfLeft("create plain cancel submission data",
    PlainCancelSubmissionData.decode(info));
}

interface LinkEncryptedCancelSubmissionDataBrand {
  readonly LinkEncryptedCancelSubmissionData: unique symbol;
}
export const LinkEncryptedCancelSubmissionData = t.brand(EncryptedKeyBase64Url,
  (n): n is t.Branded<EncryptedKeyBase64Url, LinkEncryptedCancelSubmissionDataBrand> => true,
  "LinkEncryptedCancelSubmissionData");
export type LinkEncryptedCancelSubmissionData = t.TypeOf<typeof LinkEncryptedCancelSubmissionData>;

export async function encryptCancelSubmissionData(serverKey: PublicApiKey, data: PlainCancelSubmissionData): Promise<LinkEncryptedCancelSubmissionData> {
  const sodium = await getSodium();

  const json = JSON.stringify(PlainCancelSubmissionData.encode(data));
  const buffer = Buffer.from(json, "utf8");
  const serverEncrypted = await sodium.crypto_box_seal(buffer, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted cancel submission data",
    LinkEncryptedCancelSubmissionData, serverEncrypted.toString("base64url"));
}

export async function decryptCancelSubmissionData(serverKeypair: ApiKeypair, submissionKey: LinkEncryptedCancelSubmissionData): Promise<PlainCancelSubmissionData> {
  const sodium = await getSodium();

  const buffer = Buffer.from(LinkEncryptedCancelSubmissionData.encode(submissionKey), "base64url");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);
  const json = JSON.parse(serverDecrypted.toString("utf8"));

  return UnexpectedValueError.throwIfNot("decode plain cancel submission data",
    PlainCancelSubmissionData, json);
}


// Erzeuge PublicKey aus einem SecretKey
type SecretKeyTypeFromPublicKeyBrand<TBrand> =
  TBrand extends PublicUserDataKeyBrand ? PlainSecretUserDataKey
  : TBrand extends PublicSubmissionDataKeyBrand ? PlainSecretSubmissionDataKey
  : TBrand extends PublicSubmissionLinkKeyBrand ? PlainSecretSubmissionLinkKey
  : never;

export async function getPublicKeyFromSecretKey<TBrand>(
  codec: t.BrandC<t.Type<X25519PublicKey, string, unknown>, TBrand>,
  secretKey: SecretKeyTypeFromPublicKeyBrand<TBrand>): Promise<t.Branded<X25519PublicKey, TBrand>> {
  const sodium = await getSodium();

  const publicKey = await sodium.crypto_box_publickey_from_secretkey(secretKey);

  return UnexpectedValueError.throwIfNot(`decode public key ${codec.name}`,
    codec, publicKey);
}

// EditorApiKey wird für unseren intern verwendeten Formulareditor verwendet
interface PlainEditorApiKeyBrand {
  readonly PlainEditorApiKey: unique symbol;
}
export const PlainEditorApiKey = t.brand(Base64,
  (n): n is t.Branded<Base64, PlainEditorApiKeyBrand> => true,
  "PlainEditorApiKey");
export type PlainEditorApiKey = t.TypeOf<typeof PlainEditorApiKey>;

let plainEditorApiKey: PlainEditorApiKey | null | undefined = undefined;

export function getPlainEditorApiKey(): PlainEditorApiKey | null {
  if (plainEditorApiKey !== undefined) return plainEditorApiKey;

  try {
    const value = process.env.EDITOR_API_KEY;
    if (!value) throw "The environment variable is not set.";

    return plainEditorApiKey =
      UnexpectedValueError.throwIfNot("decode plain editor api key",
        PlainEditorApiKey, value);
  } catch (err) {
    console.error(`Could not load editor api key: ${err + ''}`);
    return secretDataKey = null;
  }
}

interface EncryptedEditorApiKeyBrand {
  readonly EncryptedEditorApiKey: unique symbol;
}
export const EncryptedEditorApiKey = t.brand(EncryptedKeyBase64,
  (n): n is t.Branded<EncryptedKeyBase64, EncryptedEditorApiKeyBrand> => true,
  "EncryptedEditorApiKey");
export type EncryptedEditorApiKey = t.TypeOf<typeof EncryptedEditorApiKey>;

export async function encryptEditorApiKey(serverKey: PublicApiKey, editorApiKey: PlainEditorApiKey): Promise<EncryptedEditorApiKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(Base64.encode(editorApiKey), "base64");
  const serverEncrypted = await sodium.crypto_box_seal(buffer, serverKey);

  return UnexpectedValueError.throwIfNot("decode encrypted editor api key",
    EncryptedEditorApiKey, serverEncrypted.toString("base64"));
}

export async function decryptEditorApiKey(serverKeypair: ApiKeypair, editorApiKey: EncryptedEditorApiKey): Promise<PlainEditorApiKey> {
  const sodium = await getSodium();

  const buffer = Buffer.from(EncryptedEditorApiKey.encode(editorApiKey), "base64");
  const serverDecrypted = await sodium.crypto_box_seal_open(buffer, serverKeypair.publicKey, serverKeypair.secretKey);

  return UnexpectedValueError.throwIfNot("decode plain editor api key",
    PlainEditorApiKey, serverDecrypted.toString("base64"));
}

export async function isEncryptedEditorApiKeyValid(serverKeypair: ApiKeypair, editorApiKey: EncryptedEditorApiKey): Promise<boolean> {
  try {
    const configuredEditorApiKey = getPlainEditorApiKey();
    if (!configuredEditorApiKey) return false;

    const decryptedEditorApiKey = await decryptEditorApiKey(serverKeypair, editorApiKey);
    return configuredEditorApiKey === decryptedEditorApiKey;
  } catch {
    return false;
  }
}
